import {
  BASE_PATH,
  FRUIT_POINTS,
  NAME_MAX_LENGTH,
  PLAYER_COLORS,
  rank,
  sanitizeName,
  type Dir,
  type ErrorCode,
  type PlayerInfo,
  type RankingEntry,
  type RoomStatus,
  type ServerMsg,
} from "../src/protocol";
import { track } from "./analytics";
import { openDialog } from "./dialog";
import { h } from "./dom";
import { brandMark, siteFooter } from "./home";
import { copyIcon, exitIcon, shareIcon } from "./icons";
import { uiLangSelect } from "./langPicker";
import { Connection, type ConnectionStatus } from "./net";
import { qrSvg } from "./qr";
import { ArenaView, FRUIT_GLYPHS } from "./render";
import { shareButton, type ResultCard } from "./shareCard";
import { getName, getPlayerId, setName } from "./storage";
import { STRINGS } from "./strings";
import { formatClock } from "./timing";

const CANONICAL_ORIGIN = "https://workbrawl.com";
/** Finger travel that counts as a swipe, in CSS pixels. */
const SWIPE_PX = 24;
const TOAST_MS = 2500;
/** The clock is green until this much time is left, then shifts gradually towards red… */
const CLOCK_WARN_MS = 30_000;
/** …which it reaches here: the last seconds are solid red and blink. */
const CLOCK_FINAL_MS = 10_000;
const CLOCK_GREEN_HUE = 135;

const KEY_DIRS: Record<string, Dir> = {
  ArrowUp: 0,
  ArrowRight: 1,
  ArrowDown: 2,
  ArrowLeft: 3,
  w: 0,
  d: 1,
  s: 2,
  a: 3,
};

/** Room screen. Asks for a name first when the visitor arrived by link without one. */
export function renderRoom(root: HTMLElement, code: string): void {
  const name = sanitizeName(getName());
  if (name === null) renderNamePrompt(root, code);
  else new RoomScreen(root, code, name);
}

function renderNamePrompt(root: HTMLElement, code: string): void {
  const input = h("input", {
    id: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  const error = h("p", { class: "form-error", role: "alert" });
  const form = h(
    "form",
    { class: "card name-prompt", novalidate: true },
    h("h2", {}, STRINGS.namePromptTitle),
    h("p", { class: "muted" }, STRINGS.namePromptBody),
    h("label", { for: "name" }, STRINGS.nameLabel),
    input,
    h("button", { type: "submit", class: "btn btn-primary" }, STRINGS.enterRoom),
    error,
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = sanitizeName(input.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      input.focus();
      return;
    }
    setName(name);
    new RoomScreen(root, code, name);
  });
  root.replaceChildren(
    h("main", { class: "center-screen" }, h("h1", { class: "brand brand-large" }, brandMark()), form),
    siteFooter(),
  );
  input.focus();
}

class RoomScreen {
  private readonly connection: Connection;
  private readonly toastEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private readonly clockEl = h("strong", { class: "clock", "aria-label": STRINGS.timeLeft }, "0:00");
  private readonly arenaWrap = h("div", { class: "arena-wrap" });
  private readonly overlayEl = h("div", { class: "arena-overlay" });
  private readonly pillEl = h("div", { class: "arena-pill", hidden: true }, STRINGS.respawning);
  private readonly playersEl = h("ol", { class: "player-list" });
  private readonly view: ArenaView;

  private you = "";
  private status: RoomStatus = "lobby";
  private players = new Map<string, PlayerInfo>();
  private ranking: RankingEntry[] = [];
  /** Round length of the room, for the shared result. */
  private roundSeconds = 0;
  /** Local time at which the countdown or the round ends. */
  private deadline = 0;
  private countdownTimer: ReturnType<typeof setInterval> | undefined;
  private toastTimer: ReturnType<typeof setTimeout> | undefined;
  private scoreSignature = "";
  private fatal = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly code: string,
    name: string,
  ) {
    this.view = new ArenaView(this.arenaWrap);
    this.view.setLabel(STRINGS.appName);
    this.arenaWrap.append(this.overlayEl, this.pillEl);

    root.replaceChildren(
      this.header(),
      this.toastEl,
      h(
        "main",
        { class: "arena-layout" },
        h(
          "section",
          { class: "arena-area" },
          h("div", { class: "arena-hud" }, h("span", { class: "muted" }, STRINGS.timeLeft), this.clockEl),
          this.arenaWrap,
          fruitLegend(),
          h("p", { class: "muted arena-hint" }, STRINGS.controlsHint),
        ),
        h(
          "aside",
          { class: "panel" },
          h("section", { class: "card" }, h("h3", { class: "panel-title" }, STRINGS.scoreboard), this.playersEl),
          this.inviteCard(),
        ),
      ),
      siteFooter(),
    );

    this.bindControls();
    this.connection = new Connection(Connection.roomUrl(code, getPlayerId(), name), {
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (status) => this.onStatus(status),
    });
    this.connection.connect();
  }

  private header(): HTMLElement {
    const exit = h(
      "button",
      { type: "button", class: "btn btn-small btn-exit", "aria-haspopup": "dialog", "aria-label": STRINGS.exit },
      exitIcon(),
      // Hidden on narrow screens, where the button is icon-only.
      h("span", { class: "btn-label" }, STRINGS.exit),
    );
    exit.addEventListener("click", () => this.confirmExit());
    return h(
      "header",
      { class: "topbar" },
      h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
      h(
        "div",
        { class: "room-info" },
        h("span", { class: "muted room-label" }, STRINGS.room),
        h("strong", { class: "room-code" }, this.code),
        uiLangSelect(true),
        exit,
      ),
    );
  }

  private confirmExit(): void {
    const cancel = h("button", { type: "button", class: "btn" }, STRINGS.cancel);
    const leave = h("button", { type: "button", class: "btn btn-danger" }, exitIcon(), STRINGS.exit);
    const dialog = openDialog(
      "confirm",
      h("h2", { class: "dialog-title" }, STRINGS.exitTitle),
      h("p", { class: "muted" }, STRINGS.exitBody),
      h("div", { class: "dialog-actions" }, cancel, leave),
    );
    cancel.addEventListener("click", () => dialog.close());
    leave.addEventListener("click", () => {
      // Suppress the "connection lost" message while we leave on purpose.
      this.fatal = true;
      this.connection.stop();
      location.assign(`${BASE_PATH}/`);
    });
    cancel.focus();
  }

  /** Copies the invite link; reports through a floating message unless `onDone` handles it. */
  private async copyInvite(onDone?: (ok: boolean) => void): Promise<void> {
    const url = inviteUrl(this.code);
    const ok = await copyText(url);
    if (onDone) onDone(ok);
    else this.toast(ok ? STRINGS.linkCopiedToast : `${STRINGS.copyFailed} ${url}`);
  }

  /** Invite card under the scoreboard: title with share/copy actions on the right. */
  private inviteCard(): HTMLElement {
    const share = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.share, "aria-label": STRINGS.share, "aria-haspopup": "dialog" },
      shareIcon(),
    );
    share.addEventListener("click", () => this.openInvite());
    const copy = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.copyLink, "aria-label": STRINGS.copyLink },
      copyIcon(),
    );
    copy.addEventListener("click", () => this.copyInvite());

    return h(
      "section",
      { class: "card invite-card" },
      h(
        "div",
        { class: "invite-head" },
        h("h3", { class: "panel-title" }, STRINGS.inviteTitle),
        h("div", { class: "icon-actions" }, share, copy),
      ),
    );
  }

  /** Modal with a large QR, the link, copy and (where supported) the native share sheet. */
  private openInvite(): void {
    const url = inviteUrl(this.code);
    const copy = h("button", { type: "button", class: "btn" }, copyIcon(), STRINGS.copyLink);
    copy.addEventListener("click", () =>
      this.copyInvite((ok) => {
        copy.replaceChildren(copyIcon(), ok ? STRINGS.linkCopied : STRINGS.copyFailedShort);
        setTimeout(() => copy.replaceChildren(copyIcon(), STRINGS.copyLink), 1500);
      }),
    );
    const close = h("button", { type: "button", class: "btn" }, STRINGS.close);

    const actions: HTMLElement[] = [];
    if (typeof navigator.share === "function") {
      const native = h("button", { type: "button", class: "btn btn-primary" }, shareIcon(), STRINGS.share);
      native.addEventListener("click", () => {
        navigator.share({ title: STRINGS.appName, text: STRINGS.shareText, url }).catch(() => {
          // The share sheet was dismissed: nothing to do.
        });
      });
      actions.push(native);
    }
    actions.push(copy, close);

    const dialog = openDialog(
      "invite",
      h("h2", { class: "dialog-title" }, STRINGS.inviteTitle),
      h("div", { class: "qr-box qr-box-large" }, qrSvg(url, STRINGS.qrLabel(this.code))),
      h("p", { class: "invite-url" }, url),
      h("div", { class: "dialog-actions" }, ...actions),
    );
    close.addEventListener("click", () => dialog.close());
  }

  private bindControls(): void {
    window.addEventListener("keydown", (event) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return;
      const dir = KEY_DIRS[event.key.length === 1 ? event.key.toLowerCase() : event.key];
      if (dir === undefined || this.status !== "running") return;
      event.preventDefault(); // arrows would scroll the page
      this.turn(dir);
    });

    // Swipes: each SWIPE_PX of travel is one turn, so a finger can stay down and keep steering.
    let origin: { x: number; y: number } | null = null;
    this.arenaWrap.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse") return;
      origin = { x: event.clientX, y: event.clientY };
    });
    this.arenaWrap.addEventListener("pointermove", (event) => {
      if (!origin || this.status !== "running") return;
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_PX) return;
      this.turn(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0);
      origin = { x: event.clientX, y: event.clientY };
    });
    const end = () => (origin = null);
    this.arenaWrap.addEventListener("pointerup", end);
    this.arenaWrap.addEventListener("pointercancel", end);
  }

  private turn(dir: Dir): void {
    this.connection.send({ type: "dir", dir });
  }

  private onStatus(status: ConnectionStatus): void {
    if (this.fatal) return;
    if (status === "connecting") this.toast(STRINGS.connecting, true);
    else if (status === "reconnecting") this.toast(STRINGS.reconnecting, true);
    else if (status === "open") this.hideToast();
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case "state": {
        const previous = this.status;
        this.you = msg.you;
        this.status = msg.status;
        this.roundSeconds = msg.duration;
        // One per person per round: this is what "how much is each title used" is measured by.
        if (msg.status === "running" && previous !== "running") {
          track("round_started", { people: msg.players.length, duration: msg.duration });
        } else if (msg.status === "finished" && previous === "running") {
          track("round_finished", { people: msg.players.length });
        }
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.ranking = msg.ranking;
        this.deadline = Date.now() + msg.remainingMs;
        this.renderStatus();
        this.renderPlayers(true);
        break;
      }
      case "tick": {
        for (const [slot, score] of msg.p) {
          for (const player of this.players.values()) if (player.slot === slot) player.score = score;
        }
        const mySlot = this.players.get(this.you)?.slot ?? -1;
        this.view.show({ snakes: msg.s, food: msg.f }, mySlot);
        this.pillEl.hidden = mySlot < 0 || msg.s.some(([slot]) => slot === mySlot);
        this.setClock(msg.t);
        this.renderPlayers();
        break;
      }
      case "player_joined":
      case "player_updated":
        this.players.set(msg.player.id, msg.player);
        this.renderPlayers(true);
        if (this.status === "lobby") this.renderStatus();
        break;
      case "player_left":
        this.players.delete(msg.id);
        this.renderPlayers(true);
        if (this.status === "lobby") this.renderStatus();
        break;
      case "error":
        this.onError(msg.code);
        break;
      case "pong":
        break;
    }
  }

  private onError(code: ErrorCode): void {
    if (code === "BAD_MESSAGE") return;
    this.fatal = true;
    this.connection.stop();
    this.root.replaceChildren(
      h(
        "main",
        { class: "center-screen" },
        h("h1", { class: "brand brand-large" }, brandMark()),
        h(
          "section",
          { class: "card center-card" },
          h("p", {}, code === "ROOM_FULL" ? STRINGS.errorRoomFull : STRINGS.errorRoomNotFound),
          h("a", { href: `${BASE_PATH}/`, class: "btn btn-primary" }, STRINGS.backHome),
        ),
      ),
      siteFooter(),
    );
  }

  /** Overlay and clock for the current phase of the room. */
  private renderStatus(): void {
    clearInterval(this.countdownTimer);
    this.pillEl.hidden = true;
    this.overlayEl.hidden = this.status === "running";

    if (this.status === "lobby") {
      this.view.clear();
      this.setClock(0);
      this.overlayEl.replaceChildren(
        h("h2", {}, STRINGS.lobbyTitle),
        h("p", {}, STRINGS.lobbyBody(this.onlineCount())),
        this.startButton(STRINGS.startRound),
      );
    } else if (this.status === "countdown") {
      this.view.clear();
      const number = h("strong", { class: "countdown" });
      const update = () => {
        const next = String(Math.max(1, Math.ceil((this.deadline - Date.now()) / 1000)));
        if (number.textContent === next) return;
        number.textContent = next;
        // Restart the zoom-out for each new number (reading offsetWidth forces a reflow).
        number.classList.remove("countdown-pop");
        void number.offsetWidth;
        number.classList.add("countdown-pop");
      };
      update();
      this.countdownTimer = setInterval(update, 100);
      this.overlayEl.replaceChildren(h("p", {}, STRINGS.getReady), number);
    } else if (this.status === "finished") {
      this.setClock(0);
      this.overlayEl.replaceChildren(
        h("h2", {}, STRINGS.resultsTitle),
        h("p", { class: "results-headline" }, this.headline()),
        this.rankingList(),
        this.startButton(STRINGS.newRound),
        shareButton(() => this.resultCard()),
      );
    } else {
      this.setClock(this.deadline - Date.now());
    }
  }

  /** What "Share result" draws: the podium, worded for people who weren't in the room. */
  private resultCard(): ResultCard {
    const [first, second] = this.ranking;
    return {
      title: "SnakeBrawl",
      slug: "snakebrawl",
      accent: "#2f9e44",
      headline: !first ? STRINGS.resultsTitle : second && second.score === first.score ? STRINGS.tie : STRINGS.winner(first.name),
      podium: this.ranking.slice(0, 3).map((entry) => ({
        name: entry.name,
        score: STRINGS.points(entry.score),
        color: PLAYER_COLORS[entry.slot % PLAYER_COLORS.length]!,
      })),
      people: this.ranking.length || this.players.size,
      minutes: Math.round(this.roundSeconds / 60),
    };
  }

  private startButton(label: string): HTMLElement {
    const button = h("button", { type: "button", class: "btn btn-primary" }, label);
    button.addEventListener("click", () => this.connection.send({ type: "start" }));
    return button;
  }

  private headline(): string {
    const [first, second] = this.ranking;
    if (!first) return "";
    if (second && second.score === first.score) return STRINGS.tie;
    return first.id === this.you ? STRINGS.youWin : STRINGS.winner(first.name);
  }

  private rankingList(): HTMLElement {
    return h(
      "ol",
      { class: "ranking" },
      // The podium only: the full order is in the scoreboard next to the arena.
      ...this.ranking.slice(0, 3).map((entry, i) => {
        const row = h(
          "li",
          { class: i === 0 ? "ranking-row ranking-first" : "ranking-row" },
          h("span", { class: "ranking-pos" }, `${i + 1}`),
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(entry.name)),
          h("span", { class: "player-name" }, entry.name),
          h("span", { class: "player-score" }, STRINGS.points(entry.score)),
        );
        row.style.setProperty("--player", PLAYER_COLORS[entry.slot % PLAYER_COLORS.length]!);
        return row;
      }),
    );
  }

  /** Scoreboard, best first. Ticks arrive ~8 times a second, so it only rebuilds on changes. */
  private renderPlayers(force = false): void {
    const ordered = rank([...this.players.values()]);
    const signature = ordered.map((p) => `${p.id}:${p.score}:${p.online}`).join("|");
    if (!force && signature === this.scoreSignature) return;
    this.scoreSignature = signature;
    this.playersEl.replaceChildren(
      ...ordered.map((p) => {
        const row = h(
          "li",
          { class: p.online ? "player" : "player player-offline" },
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(p.name)),
          h(
            "span",
            { class: "player-name" },
            p.name,
            p.id === this.you ? h("span", { class: "tag" }, STRINGS.you) : null,
            p.online ? null : h("span", { class: "tag" }, STRINGS.offline),
          ),
          h("span", { class: "player-score", title: STRINGS.points(p.score) }, String(p.score)),
        );
        row.style.setProperty("--player", PLAYER_COLORS[p.slot % PLAYER_COLORS.length]!);
        return row;
      }),
    );
  }

  private onlineCount(): number {
    return [...this.players.values()].filter((p) => p.online).length;
  }

  private setClock(ms: number): void {
    this.clockEl.textContent = formatClock(ms);
    const running = this.status === "running";
    this.clockEl.classList.toggle("clock-running", running);
    this.clockEl.classList.toggle("clock-final", running && ms <= CLOCK_FINAL_MS);
    // Hue 135 (green) → 0 (red), passing through yellow and orange on the way.
    const left = Math.min(1, Math.max(0, (ms - CLOCK_FINAL_MS) / (CLOCK_WARN_MS - CLOCK_FINAL_MS)));
    this.clockEl.style.setProperty("--clock-hue", String(Math.round(CLOCK_GREEN_HUE * left)));
  }

  private toast(text: string, sticky = false): void {
    clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    if (!sticky) this.toastTimer = setTimeout(() => this.hideToast(), TOAST_MS);
  }

  private hideToast(): void {
    clearTimeout(this.toastTimer);
    this.toastEl.hidden = true;
  }
}

/** What each fruit is worth, e.g. "🍎 🍌 1 point · 🍒 3 points". */
function fruitLegend(): HTMLElement {
  const byPoints = new Map<number, string[]>();
  FRUIT_POINTS.forEach((points, kind) => byPoints.set(points, [...(byPoints.get(points) ?? []), FRUIT_GLYPHS[kind]!]));
  const parts = [...byPoints].map(([points, glyphs]) => `${glyphs.join(" ")} ${STRINGS.fruitPoints(points)}`);
  return h("p", { class: "arena-legend" }, parts.join("  ·  "));
}

/** Clipboard API where available, with the legacy selection path as a fallback. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const area = h("textarea", { readonly: true, class: "visually-hidden" });
  area.value = text;
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}

/**
 * Invite links use the canonical domain, except when running locally or on
 * *.workers.dev, where the canonical link would point to a different deployment.
 */
function inviteUrl(code: string): string {
  const host = location.hostname;
  const isLocal =
    host === "localhost" ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host.endsWith(".workers.dev");
  return `${isLocal ? location.origin : CANONICAL_ORIGIN}${BASE_PATH}/r/${code}`;
}
